import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Users, Crown, ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/lang";
import NavBar from "@/components/NavBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GamesLeaderboard() {
  const { lang } = useLang();
  const [tab, setTab] = useState("players"); // players | teams
  const [scope, setScope] = useState("global"); // global | weekly | monthly
  const [data, setData] = useState(null);
  const T = lang === "fr" ? FR : EN;

  useEffect(() => {
    const endpoint = tab === "players" ? "leaderboard/players" : "leaderboard/teams";
    axios.get(`${API}/games/${endpoint}`, { params: { scope, limit: 30 } })
      .then(r => setData(r.data));
  }, [tab, scope]);

  const top3 = (data?.top || []).slice(0, 3);
  const rest = (data?.top || []).slice(3);

  return (
    <div className="min-h-screen bg-white" data-testid="games-leaderboard-page">
      <NavBar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/games" className="text-sm text-zinc-500 hover:text-[#8b6f3d] flex items-center gap-1"><ArrowLeft size={14} /> {T.back}</Link>
        <h1 className="font-serif-mystery text-4xl mt-3 mb-6">🏆 {T.title}</h1>

        <div className="flex flex-wrap gap-2 mb-6">
          <button data-testid="tab-players" onClick={() => setTab("players")} className={`px-4 h-10 rounded-full text-sm font-medium ${tab === "players" ? "bg-[#8b6f3d] text-white" : "border border-zinc-300 text-zinc-700"}`}>🥇 {T.players}</button>
          <button data-testid="tab-teams" onClick={() => setTab("teams")} className={`px-4 h-10 rounded-full text-sm font-medium ${tab === "teams" ? "bg-[#8b6f3d] text-white" : "border border-zinc-300 text-zinc-700"}`}>🏆 {T.teams}</button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["global", "weekly", "monthly"].map(s => (
            <button key={s} data-testid={`scope-${s}`} onClick={() => setScope(s)}
              className={`px-3 h-8 rounded-full text-xs font-mono-data uppercase tracking-widest ${scope === s ? "bg-zinc-900 text-white" : "border border-zinc-200 text-zinc-600"}`}>
              {T[s]}
            </button>
          ))}
        </div>

        {/* Podium */}
        {top3.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-8" data-testid="podium">
            {[top3[1], top3[0], top3[2]].filter(Boolean).map((e, i) => {
              const rank = e.rank;
              const heights = { 1: "h-40", 2: "h-32", 3: "h-24" };
              const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
              return (
                <div key={e.rank} className={`text-center flex flex-col justify-end`}>
                  <div className="mb-2">
                    {e.picture ? <img src={e.picture} alt="" className="w-14 h-14 rounded-full mx-auto object-cover border-2 border-[#8b6f3d]" />
                      : <div className="w-14 h-14 mx-auto rounded-full bg-zinc-200" />}
                    <div className="font-medium text-sm mt-1 truncate">{e.username || e.name}</div>
                    <div className="text-xs text-[#8b6f3d]">{(e.score || e.total_score || 0).toLocaleString()}</div>
                  </div>
                  <div className={`${heights[rank]} rounded-t-xl bg-gradient-to-t from-[#8b6f3d] to-amber-300 flex items-center justify-center text-4xl`}>
                    {medals[rank]}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Rest */}
        <div className="space-y-2" data-testid="leaderboard-rest">
          {rest.map(e => (
            <div key={e.rank || e.id} className="flex items-center gap-3 p-3 rounded-xl border border-zinc-200 hover:border-[#8b6f3d] transition-colors">
              <div className="w-8 text-center font-mono-data text-sm text-zinc-500">#{e.rank}</div>
              {e.picture && <img src={e.picture} alt="" className="w-9 h-9 rounded-full object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{e.username || e.name}</div>
                {e.country && <div className="text-xs text-zinc-500">{e.country}</div>}
                {e.members_count !== undefined && <div className="text-xs text-zinc-500">{e.members_count} {T.members}</div>}
              </div>
              <div className="text-right">
                <div className="font-serif-mystery text-lg text-[#8b6f3d]">{(e.score || e.total_score || 0).toLocaleString()}</div>
                <div className="text-[10px] font-mono-data uppercase tracking-widest text-zinc-500">{T.points}</div>
              </div>
            </div>
          ))}
          {rest.length === 0 && top3.length === 0 && (
            <p className="text-center text-zinc-500 py-10" data-testid="lb-empty">{T.noPlayers}</p>
          )}
        </div>
      </div>
    </div>
  );
}
const FR = { back: "Retour aux jeux", title: "Classements", players: "Joueurs", teams: "Équipes", global: "Mondial", weekly: "Hebdo", monthly: "Mensuel", points: "Points", members: "membres", noPlayers: "Aucun joueur pour le moment. Sois le premier à établir un score !" };
const EN = { back: "Back to games", title: "Leaderboards", players: "Players", teams: "Teams", global: "Global", weekly: "Weekly", monthly: "Monthly", points: "Points", members: "members", noPlayers: "No players yet. Be the first to set a score!" };
